import { InfoOutline } from '@mui/icons-material';
import { Card, CardContent, Divider, Tooltip, Typography } from '@mui/material';
import ErrorBoundary from 'components/ErrorBoundary';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import DatePicker from 'further-ui/components/DatePicker';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { revaluation as rules } from 'further-ui/validations';
import { sumBy } from 'lodash';
import { Controller } from 'react-hook-form';
import { RevaluationExternalState } from '../FormWrapper';
import useRevaluationsFormHandlers from '../hooks/useRevaluationFormHandlers';
import useRevaluations from '../hooks/useRevaluations';
import CurrentValuationRow from './CurrentValuationRow';
import { HoldingClassInvestmentTotal } from './types';

type OverallRevaluationProps = {
  assetName: string;
  holdingClassInvestmentTotals: Array<HoldingClassInvestmentTotal>;
  viewOnly?: boolean;
};

const OverallRevaluationSection: React.FC<OverallRevaluationProps> = ({
  assetName,
  holdingClassInvestmentTotals,
  viewOnly,
}) => {
  const { formMethods } = useFormWithStateContext<
    rules.RevaluationFormType,
    RevaluationExternalState
  >();

  const { watch, control, getValues, setValue } = formMethods;
  const { latestRevaluation } = useRevaluations({
    watch,
    control,
    getValues,
  });

  const {
    updateAllNewRevaluationsDate,
    handleOverallHoldValueChange,
    handleOverallUnitPriceChange,
    setFieldFocus,
  } = useRevaluationsFormHandlers({
    watch,
    setValue,
  });

  const holdingClassCurrency: CurrencyCode =
    holdingClassInvestmentTotals[0]?.currency;

  const assetHasMoreThanOneCurrency = holdingClassInvestmentTotals.some(
    (holdingClass) => holdingClass.currency !== holdingClassCurrency,
  );

  const numberOfUnits = sumBy(
    holdingClassInvestmentTotals,
    'currentNumberOfUnits',
  );

  const latestRevaluationDateFormatted =
    (latestRevaluation &&
      new CalendarDay(latestRevaluation.ledgerDate).toReadable()) ??
    '-';

  //These values are static as they are not affected by new revaluations
  const currentHoldValueStatic = sumBy(
    holdingClassInvestmentTotals,
    'currentHoldValue',
  );

  const averageUnitPriceStatic =
    numberOfUnits && currentHoldValueStatic
      ? currentHoldValueStatic / numberOfUnits
      : undefined;

  const averageUnitPriceStaticFormatted = !assetHasMoreThanOneCurrency
    ? numberToCurrencyString(averageUnitPriceStatic, {
        currency: holdingClassCurrency,
        unlimitedDp: true,
      })
    : '-';

  const currentHoldValueStaticFormatted = !assetHasMoreThanOneCurrency
    ? numberToCurrencyString(currentHoldValueStatic, {
        currency: holdingClassCurrency,
      })
    : '-';

  const newHoldValue = watch('currentHoldValue') ?? 0;
  const newUnitPrice = watch('currentUnitPrice') ?? 0;
  const newRevaluationDate = watch('currentRevaluationDate') ?? new Date();

  return (
    <ErrorBoundary>
      <Column spacing="lg">
        <Card variant="outlined">
          <CardContent>
            <Column spacing="lg">
              <Row centered spacing="xxs">
                <Typography variant="h6">
                  {`Total ${assetName} asset value`}
                </Typography>
                <Tooltip title="Total asset revaluation should only be used when the unit price is the same across all holding classes.">
                  <InfoOutline fontSize="inherit" />
                </Tooltip>
              </Row>
              <Row fullWidth justify="center" spacing="lg">
                <FormField label="New hold value" layout="horizontal">
                  <Controller
                    name="currentHoldValue"
                    control={control}
                    render={({
                      field: { ref, onChange, ...rest },
                      fieldState: { error },
                    }) => (
                      <NumberInput
                        fullWidth
                        {...rest}
                        onChange={(value) => {
                          onChange(value);
                          handleOverallHoldValueChange(
                            value ?? 0,
                            numberOfUnits,
                          );
                        }}
                        onFocus={() => setFieldFocus('holdValue')}
                        currency={holdingClassCurrency}
                        decimals={2}
                        disabled={viewOnly}
                        error={!!error}
                        helperText={error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormField>
                <FormField label="New unit price" layout="horizontal">
                  <Controller
                    name="currentUnitPrice"
                    control={control}
                    render={({
                      field: { ref, onChange, ...rest },
                      fieldState: { error },
                    }) => (
                      <NumberInput
                        fullWidth
                        {...rest}
                        onChange={(value) => {
                          onChange(value);
                          handleOverallUnitPriceChange(
                            value ?? 0,
                            numberOfUnits,
                          );
                        }}
                        onFocus={() => setFieldFocus('unitPriceTcy')}
                        currency={holdingClassCurrency}
                        decimals={10}
                        disabled={viewOnly}
                        error={!!error}
                        helperText={error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormField>
                <FormField label="Revaluation date" layout="horizontal">
                  <Controller
                    name="currentRevaluationDate"
                    control={control}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        fullWidth
                        value={new CalendarDay(value ?? new Date())}
                        onChange={(date) => {
                          onChange(date?.toUTCDate());
                          updateAllNewRevaluationsDate(
                            date?.toUTCDate() ?? new Date(),
                          );
                        }}
                        minDate={latestRevaluation?.ledgerDate}
                        error={!!error}
                        helperText={error?.message}
                        size="small"
                        disableFuture
                        disabled={viewOnly}
                      />
                    )}
                  />
                </FormField>
              </Row>
              <Divider />
              <CurrentValuationRow
                currentValuation={{
                  currentHoldValue: currentHoldValueStaticFormatted,
                  averageUnitPriceTcy: averageUnitPriceStaticFormatted,
                  latestRevaluationDate: latestRevaluationDateFormatted,
                  currentNumberOfUnits: numberOfUnits,
                }}
                newValuation={{
                  holdValue: !assetHasMoreThanOneCurrency
                    ? numberToCurrencyString(newHoldValue, {
                        currency: holdingClassCurrency,
                      })
                    : '-',
                  averageUnitPriceTcy: !assetHasMoreThanOneCurrency
                    ? numberToCurrencyString(newUnitPrice, {
                        currency: holdingClassCurrency,
                        unlimitedDp: true,
                      })
                    : '-',
                  numberOfUnits,
                  revaluationDate: newRevaluationDate
                    ? new CalendarDay(newRevaluationDate).toReadable()
                    : '-',
                }}
              />
            </Column>
          </CardContent>
        </Card>
      </Column>
    </ErrorBoundary>
  );
};

export default OverallRevaluationSection;
